import lockIcon from '@assets/svg/LockIcon.svg';
import { CodeInput, CustomButton, CustomDrawer } from '@components';
import { HttpsOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { COLORS } from '@styles';
import { appContext } from '@utils';
import { useFormik } from 'formik';
import { CSSProperties, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';

const codeValidationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
});

interface ConfirmPhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  callBackFunction: () => void;
}

const styles = {
  image: {
    width: "8.5rem",
  } as CSSProperties,
}

const makeStyles = () => ({
  returnedCodeCard: {
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: COLORS.NEUTRAL_DARK_2,
    border: `1px solid ${COLORS.BORDER_DARK}`,
  } as CSSProperties,
  returnedCodeText: {
    fontFamily: 'Urbanist',
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '4px',
    color: COLORS.NEUTRAL_WHITE,
  } as CSSProperties,
  copyButton: {
    textTransform: 'none',
    fontWeight: 600,
    color: COLORS.ELEKTRAFI_NEUTRAL,
  } as CSSProperties,
});

const sx = makeStyles();

/**
 * Drawer for confirming the current phone number by entering a 6-digit verification code.
 */
export default function ConfirmPhoneVerificationModal({
  isOpen,
  onClose,
  callBackFunction,
}: ConfirmPhoneVerificationModalProps) {
  const [copied, setCopied] = useState(false);
  const { reauthChallengeStore, userStore } = useContext(appContext);
  const {
    resendReauthCode,
    resendReauthCodeResponse,
    validateReauthCode,
    validateReauthCodeResponse,
    getOtpCode,
  } = reauthChallengeStore;

  const userPhone = userStore.userInfo.user.phone ?? '';

  const otpCode = getOtpCode();

  const initialValues = { code: '' };

  const { setFieldValue, handleSubmit, values, errors, touched, resetForm } = useFormik({
    initialValues,
    validationSchema: codeValidationSchema,
    onSubmit: async (formValues) => {
      const success = await validateReauthCode(formValues.code);
      if (success === true) {
        callBackFunction();
        onClose();
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
  });

  useEffect(() => {
    resetForm();
  }, [isOpen, resetForm]);

  const handleResendCode = async () => {
    await resendReauthCode();
    setFieldValue('code', '');
  };

  const handleCodeComplete = () => {
    handleSubmit();
  };

  const handleCopyCode = async () => {
    if (!otpCode) return;
    try {
      await navigator.clipboard.writeText(otpCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };



  const isSubmitting =
    validateReauthCodeResponse.status === 'pending' ||
    resendReauthCodeResponse.status === 'pending';


  return (
    <CustomDrawer
      open={isOpen}
      onClose={onClose}
      headerLabel="Confirm your Current Phone Number"
      headerIcon={<HttpsOutlined sx={{ color: COLORS.ELEKTRAFI_CYAN_NEUTRAL }} />}
      sx={{
        zIndex: 1400,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '90%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={lockIcon}
            alt=""
            aria-hidden
            style={styles.image}
          />
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography component="p" style={{
              fontFamily: "urbanist",
              fontSize: "1.25rem",
              fontWeight: 700,
              textAlign: "center",
              marginBottom: "1.5rem"
            }}>
              Enter Verification Code
            </Typography>
            <Typography
              component="p"
              style={{
                fontSize: '0.875rem',
                fontWeight: 400,
                textAlign: 'center',
                marginBottom: '2rem',
              }}
            >
              Please enter the <b>6-digit code</b> that we sent to (***) ***-{userPhone.slice(-4)} to finish the
              phone verification and proceed with the change.
            </Typography>
          </Box>

          <Box>
            <Box>
              <CodeInput
                value={values.code}
                onChange={(value) => setFieldValue('code', value)}
                variant="dark"
              />
              {touched.code && errors.code && (
                <Typography
                  component="p"
                  color="error"
                  sx={{ fontSize: '0.75rem', mt: 0.5, textAlign: 'center' }}
                >
                  {errors.code}
                </Typography>
              )}
              {otpCode && (
                <Box sx={sx.returnedCodeCard}>
                  <Typography component="span" sx={sx.returnedCodeText}>
                    {otpCode}
                  </Typography>
                  <Button size="small" onClick={handleCopyCode} sx={sx.copyButton} variant="text">
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
              )}
              <Button
                sx={{
                  color: COLORS.ELEKTRAFI_NEUTRAL,
                  width: '100%',
                  height: '2.5rem',
                  marginTop: '1rem',
                }}
                type="button"
                onClick={handleResendCode}
                disabled={isSubmitting}
              >
                Resend Code
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ marginTop: 'auto', paddingInline: '0.5rem' }}>
          <CustomButton
            variant="default"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleCodeComplete}
          >
            Verify Code
          </CustomButton>
        </Box>
      </Box>
    </CustomDrawer>
  );
}


